import { FC } from 'react'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Home from 'features/home/Home'
import Layout from 'components/layout/Layout'
import { shuffleArray } from 'utils/helpers'
import { PROJECTS } from 'components/projects/constants'
import { HomeProps as Props } from 'features/home/types'
import { fetchProducers } from 'api/producer'
import { Producer } from 'types/producer'
import { HERO_IMAGES_FR, HERO_IMAGES_NL } from 'features/home/constants'
import { getEncyclopediaSubCategories } from 'api/encyclopedia'
import { CombinedCategories } from 'components/encyclopedia/navigation-menu/types'
import { routes } from 'utils/routes'
import { Language } from 'types/language'

const HomePage: FC<Props> = (props) => {
  return (
    <Layout isHome>
      <Home {...props} />
    </Layout>
  )
}

export const getStaticProps = async ({ locale }) => {
  const producers = await fetchProducers(locale)
  const projects = shuffleArray(PROJECTS).slice(0, 3)
  const subCategories = await getEncyclopediaSubCategories()
  const categories = subCategories.reduce((prev: CombinedCategories, curr) => {
    if (!prev) {
      prev = {}
    }

    // if the category has no title or is not there, then skip the entry
    if (!curr?.category?.title) {
      return prev
    }

    const subCategorySlug = routes(Language.DUTCH, '', [curr.category.slug, curr.slug]).encyclopediaSubCategory

    // if the category is not in the return value yet, then add it
    if (!prev[curr.category.title]) {
      return {
        ...prev,
        [curr.category.title]: {
          slug: routes(Language.DUTCH, curr.category.slug).encyclopediaCategory,
          color: curr.category.color,
          subCategories: [{ title: curr.title, slug: subCategorySlug }]
        }
      }
    }

    // add the subCategory to the category object
    prev[curr.category.title].subCategories.push({ title: curr.title, slug: subCategorySlug })

    return prev
  }, {})

  /**
   * Returns a random hero image
   */
  const randomHeroImage = (): string => {
    // Define which backgrounds to be used based on the given app
    const backgroundsToBeReturned = locale === 'nl' ? HERO_IMAGES_NL : HERO_IMAGES_FR

    // Return a random background
    return backgroundsToBeReturned[Math.floor(backgroundsToBeReturned.length * Math.random())]
  }

  return {
    props: {
      categories: categories,
      producers: producers.filter((prod: Producer) => prod.isFeatured[locale]),
      projects,
      heroImage: randomHeroImage(),
      ...(await serverSideTranslations(locale))
    },
    revalidate: 600 // re-generate page when a request comes in, once every 10 minutes
  }
}

export default HomePage
